import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import useAppSelector from '../../../../state/redux/hooks/index'
import Miniheader from '../../../../components/layout/MiniHeader/index'
import Pagination from '../../../../components/common/Pagination/Pagination'
import GridTable from '../../../../components/common/GridTable/GridTable'
import { List, ListItemButton, ListItemText, Avatar, ListItemAvatar } from '@mui/material'
import Typography from '../../../../components/common/Typography'
import Button from '../../../../components/common/Button'
import Card from '../../../../components/common/Card/index'
import Backdrop from '../../../../components/common/Backdrop/Backdrop'
import { TiThumbsOk, TiThumbsUp,TiThumbsDown } from 'react-icons/ti'
import { FaSliders, FaCirclePlus } from 'react-icons/fa6'
import { IoIosArrowDown } from 'react-icons/io'
import Select from '../../../../components/common/Select'
import {
  fetchAllSurveyResponses,
  fetchTotalSurveyResponses,
  fetchSurveyResponsesMoodScale
} from '../../../../state/redux/actions/surveyActions'
import { setSurveyFilters } from '../../../../state/redux/slices/surveySlice'
import { formatDistanceToNow } from 'date-fns'

const columns = [
  { colname: 'Review', colSpan: 'col-span-4', colStyle: 'flex justify-start' },
  { colname: 'Rating', colSpan: 'col-span-1' },
  { colname: 'Respondent', colSpan: 'col-span-1' },
  { colname: 'Survey type', colSpan: 'col-span-1' },
]

const SurveyReview = () => {
  const dispatch = useDispatch()
  const { 
    allSurveyResponses, 
    totalResponses, 
    moodScaleData, 
    loading, 
    filters 
  } = useAppSelector(state => state.surveys)
  
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const [toastOpen, setToastOpen] = useState(false)
  const [selectedUserFilter, setSelectedUserFilter] = useState('allUsers')
  const [selectedSurveyFilter, setSelectedSurveyFilter] = useState('appExperience')
  const [formOpen, setFormOpen] = useState(false)
  const [selectedResponse, setSelectedResponse] = useState(null)

  useEffect(() => {
    dispatch(fetchAllSurveyResponses())
    dispatch(fetchTotalSurveyResponses())
    dispatch(fetchSurveyResponsesMoodScale())
  }, [])

  const handleFilterChange = e => {
    const { name, value } = e.target
    if (name === 'user') {
      setSelectedUserFilter(value)
    } else if (name === 'surveyType') {
      setSelectedSurveyFilter(value)
    }
    dispatch(setSurveyFilters({ [name]: value }))
  }

  const handleRefresh = () => {
    setCurrentPage(1)
    dispatch(fetchAllSurveyResponses())
    dispatch(fetchTotalSurveyResponses())
    dispatch(fetchSurveyResponsesMoodScale())
  }

  // Get filtered responses based on current filters
  const filteredResponses = allSurveyResponses?.filter(response => {
    const userTypeMatch = selectedUserFilter === 'allUsers' || 
      response.respondent.userType.toLowerCase().includes(selectedUserFilter.toLowerCase())
    const surveyTypeMatch = selectedSurveyFilter === 'appExperience' || 
      response.surveyType.toLowerCase().includes(selectedSurveyFilter.toLowerCase().replace('experience', ''))
    return userTypeMatch && surveyTypeMatch
  }) || []

  // Calculate pagination
  const totalItems = filteredResponses.length
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentResponses = filteredResponses.slice(startIndex, endIndex)
  return (
    <>
      <Miniheader title="Review" />
      <div className="ml-[250px] p-8 max-w-7xl">
        <div className="flex justify-between">
          <div className="flex justify-start gap-4 py-4">
            <Card className="mt-[4px] w-[200px] border border-gray-300 shadow-none">
              <div className="flex flex-col p-4">
                <Typography variant="h4" className="text-gray-500">
                  Total Response
                </Typography>
                <Typography>{totalResponses?.data?.[0]?.total || 0}</Typography>
              </div>
            </Card>
            <Card className="mt-[4px] w-[260px] border border-gray-300 shadow-none">
              <div className="flex flex-col p-4">
                <Typography variant="h4" className="text-gray-500">
                  Total Mood Scale
                </Typography>
                <div className="flex mb-1 gap-2 items-center divide-x divide-gray-500">
                  <div className="flex flex-col">
                    <div className="flex gap-1 items-center">
                      <Typography>Great</Typography>
                      <TiThumbsUp className="text-primary text-3xl" />
                    </div>
                    <Typography align="center">{moodScaleData?.[0]?.GREAT || 0}</Typography>
                  </div>
                  <div className="flex flex-col">
                    <div className="px-1 flex gap-1 items-center">
                      <Typography>Okay</Typography>
                      <TiThumbsOk className="text-primary text-3xl" />
                    </div>
                    <Typography align="center">{moodScaleData?.[0]?.OK || 0}</Typography>
                  </div>
                  <div className="flex flex-col">
                    <div className="px-1 flex gap-1 items-center">
                      <Typography>Bad</Typography>
                      <TiThumbsDown className="text-red-500 text-3xl" />
                    </div>
                    <Typography align="center">{moodScaleData?.[0]?.BAD || 0}</Typography>
                  </div>
                </div>
              </div>
            </Card>
          </div>
          <div className="mt-1 flex flex-col justify-end  p-4">
            <div className="flex py-2 gap-6">
              <div className="flex gap-1">
                <Button size="small">
                  <FaSliders />
                </Button>
                <Select
                  id="user"
                  name="user"
                  value={selectedUserFilter}
                  onChange={handleFilterChange}
                  icon={<IoIosArrowDown />}
                  options={[
                    { value: 'allUsers', label: 'All Users' },
                    { value: 'customer', label: 'Customer' },
                    { value: 'agent', label: 'Agent' },
                    { value: 'other', label: 'Other' },
                  ]}
                  required
                  className="border-zinc-900"
                />
              </div>
              <Button onClick={() => setFormOpen(prev => !prev)}>
                <FaCirclePlus className="text-xl mr-2" />
                <Typography>Fill Survey</Typography>
              </Button>
            </div>
            <div className="flex py-2 items-center gap-2">
              <label htmlFor="questionType" className="whitespace-nowrap">
                Survey type
              </label>
              <Select
                id="surveyType"
                name="surveyType"
                value={selectedSurveyFilter}
                onChange={handleFilterChange}
                options={[
                  { value: 'appExperience', label: 'App Experience' },
                  { value: 'buyingExperience', label: 'Buying Experience' },
                  { value: 'rentingExperience', label: 'Renting Experience' },
                  { value: 'generalExperience', label: 'General Experience' },
                ]}
                icon={<IoIosArrowDown />}
                required
                size="small"
                className="mb-2 border-x-0 border-t-0 rounded-none focus:border-x-0 focus:border-t-0"
              />
            </div>
          </div>
        </div>
        <div className="mt-2">
          <Typography>How is your app experience like?</Typography>
        </div>
        <div className="my-2">
          <Pagination
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            totalItems={totalItems}
            onPageChange={setCurrentPage}
            onRefresh={handleRefresh}
            justify="end"
          />
        </div>
        <GridTable columns={columns} gridCols="grid-cols-7">
          {loading ? (
            <div className="col-span-7 text-center py-8">
              <Typography variant="body" className="text-gray-500">
                Loading survey responses...
              </Typography>
            </div>
          ) : currentResponses.length === 0 ? (
            <div className="col-span-7 text-center py-8">
              <Typography variant="body" className="text-gray-500">
                No survey responses found
              </Typography>
            </div>
          ) : (
            currentResponses.map((response, index) => (
              <ListItemButton 
                key={index} 
                component="div" 
                className="col-span-7 hover:bg-gray-50 border-b border-gray-100" 
                onClick={() => {
                  setSelectedResponse(response)
                  setToastOpen(prev => !prev)
                }}
              >
                <div className="grid grid-cols-7 gap-6 w-full py-4">
                  <div className="col-span-4">
                    <div className="flex gap-3">
                      <ListItemAvatar>
                        <Avatar 
                          src={response.respondent.avatar || ''} 
                          alt={response.respondent.name}
                          className="w-10 h-10"
                        >
                          {response.respondent.name?.charAt(0) || 'U'}
                        </Avatar>
                      </ListItemAvatar>
                      <div className="flex flex-col flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Typography variant="h4" className="font-medium text-gray-900">
                            {response.respondent.name}
                          </Typography>
                          <Typography variant="caption" className="text-gray-500">
                            {formatDistanceToNow(new Date(response.submittedAt), { addSuffix: true })}
                          </Typography>
                        </div>
                        <Typography variant="body" className="text-gray-700 text-sm leading-relaxed">
                          {response.comment || 'Quality and received property at good price with top-notch customer service.'}
                        </Typography>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center col-span-1 h-full">
                    <div className="flex items-center gap-1">
                      {response.display.stars ? (
                        <div className="flex">
                          {response.display.stars.map((star, starIndex) => (
                            <span key={starIndex} className={star.filled ? 'text-yellow-500' : 'text-gray-300'}>
                              ★
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          {response.display.label === 'Great' && <span className="text-green-500">👍</span>}
                          {response.display.label === 'Ok' && <span className="text-blue-500">👌</span>}
                          {response.display.label === 'Bad' && <span className="text-red-500">👎</span>}
                          <Typography variant="body" className="text-gray-700 text-sm font-medium">
                            {response.display.label}
                          </Typography>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center col-span-1 h-full">
                    <Typography variant="body" className="text-gray-700 text-sm">
                      {response.respondent.userType}
                    </Typography>
                  </div>
                  
                  <div className="flex items-center col-span-1 h-full">
                    <Typography variant="body" className="text-gray-700 text-sm truncate">
                      {response.surveyType.replace('_', ' ').replace(/([A-Z])/g, ' $1').trim()}
                    </Typography>
                  </div>
                </div>
              </ListItemButton>
            ))
          )}
        </GridTable>

        {/* Survey Response Detail Modal */}
        {toastOpen && selectedResponse && (
          <Backdrop isOpen={toastOpen} onClose={() => setToastOpen(false)}>
            <div className="bg-white p-6 rounded-lg max-w-2xl w-full mx-4">
              <h2 className="text-xl font-semibold mb-4">Survey Response Details</h2>
              <List>
                <ListItemButton className="hover:bg-transparent">
                  <ListItemAvatar>
                    <Avatar src={selectedResponse.respondent.avatar || '/assets/images/house.jpg'} alt="profile">
                      {selectedResponse.respondent.name?.charAt(0) || 'U'}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={selectedResponse.respondent.name}
                    secondary={formatDistanceToNow(new Date(selectedResponse.submittedAt), { addSuffix: true })}
                  />
                </ListItemButton>
              </List>
              <div className="flex flex-col space-y-4 mt-4">
                <div>
                  <Typography variant="h4" className="font-semibold">Comment:</Typography>
                  <Typography>{selectedResponse.comment || 'No comment provided'}</Typography>
                </div>
                <div>
                  <Typography variant="h4" className="font-semibold">Rating:</Typography>
                  <div className="flex items-center gap-2">
                    {selectedResponse.display.stars ? (
                      <div className="flex">
                        {selectedResponse.display.stars.map((star, starIndex) => (
                          <span key={starIndex} className={star.filled ? 'text-yellow-500 text-2xl' : 'text-gray-300 text-2xl'}>
                            ★
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        {selectedResponse.display.label === 'Great' && <span className="text-2xl">👍</span>}
                        {selectedResponse.display.label === 'Ok' && <span className="text-2xl">👌</span>}
                        {selectedResponse.display.label === 'Bad' && <span className="text-2xl">👎</span>}
                        <Typography className="font-medium">{selectedResponse.display.label}</Typography>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <Typography variant="h4" className="font-semibold">User Type:</Typography>
                  <Typography>{selectedResponse.respondent.userType}</Typography>
                </div>
                <div>
                  <Typography variant="h4" className="font-semibold">Survey Type:</Typography>
                  <Typography>{selectedResponse.surveyType.replace('_', ' ').replace(/([A-Z])/g, ' $1').trim()}</Typography>
                </div>
                {selectedResponse.property && (
                  <div>
                    <Typography variant="h4" className="font-semibold">Property:</Typography>
                    <Typography>{selectedResponse.property.title || selectedResponse.property.id || 'N/A'}</Typography>
                  </div>
                )}
                <div className="flex justify-end gap-2 mt-4">
                  <Button onClick={() => setToastOpen(false)}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </Backdrop>
        )}
      </div>
    </>
  )
}

export default SurveyReview